import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
} from '@mui/material';
import {
  Business,
  Email,
  Lock,
  Person,
  CreditCard,
  Check,
  MusicNote,
  Group,
  Storage,
  Speed,
  Security,
  Support,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { loadStripe } from '@stripe/stripe-js';

// Stripe public key from environment variables
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

const steps = ['Studio Details', 'Admin Account', 'Choose Plan', 'Payment'];

// Validation schemas for each step
const studioDetailsSchema = yup.object({
  studioName: yup
    .string()
    .required('Studio name is required')
    .min(2, 'Studio name must be at least 2 characters')
    .max(255, 'Studio name must be less than 255 characters'),
  companyName: yup
    .string()
    .max(255, 'Company name must be less than 255 characters'),
  studioDescription: yup
    .string()
    .max(1000, 'Description must be less than 1000 characters'),
});

const adminAccountSchema = yup.object({
  adminFirstName: yup
    .string()
    .required('First name is required')
    .min(1, 'First name must be at least 1 character')
    .max(100, 'First name must be less than 100 characters'),
  adminLastName: yup
    .string()
    .required('Last name is required')
    .min(1, 'Last name must be at least 1 character')
    .max(100, 'Last name must be less than 100 characters'),
  adminEmail: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  adminPassword: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('adminPassword')], 'Passwords must match'),
});

const planSelectionSchema = yup.object({
  plan: yup
    .string()
    .required('Please select a subscription plan')
    .oneOf(['BASIC', 'PROFESSIONAL', 'ENTERPRISE'], 'Invalid plan selected'),
});

const billingDetailsSchema = yup.object({
  billingEmail: yup
    .string()
    .email('Please enter a valid email address'),
  taxId: yup
    .string()
    .max(100, 'Tax ID must be less than 100 characters'),
  billingAddress: yup.object({
    line1: yup.string(),
    line2: yup.string(),
    city: yup.string(),
    state: yup.string(),
    postalCode: yup.string(),
    country: yup.string().length(2, 'Must be a 2-letter country code'),
  }),
});

interface StudioSignupFormData {
  // Studio details
  studioName: string;
  studioDescription?: string;
  companyName?: string;
  
  // Admin account
  adminEmail: string;
  adminPassword: string;
  confirmPassword: string;
  adminFirstName: string;
  adminLastName: string;
  
  // Plan selection
  plan: 'BASIC' | 'PROFESSIONAL' | 'ENTERPRISE';
  
  // Billing details
  billingEmail?: string;
  taxId?: string;
  billingAddress?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

interface PlanInfo {
  id: string;
  name: string;
  price: number;
  features: string[];
  engineers: number;
  storage: string;
  recommended?: boolean;
}

export const StudioSignup: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const navigate = useNavigate();

  // Form setup for current step
  const getSchemaForStep = (step: number) => {
    switch (step) {
      case 0:
        return studioDetailsSchema;
      case 1:
        return adminAccountSchema;
      case 2:
        return planSelectionSchema;
      case 3:
        return billingDetailsSchema;
      default:
        return yup.object({});
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
    getValues,
  } = useForm<StudioSignupFormData>({
    resolver: yupResolver(getSchemaForStep(activeStep)),
    mode: 'onChange',
  });

  const selectedPlan = watch('plan');

  // Fetch available plans on component mount
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/studio-signup/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      // Use default plans as fallback
      setPlans([
        {
          id: 'BASIC',
          name: 'Basic',
          price: 29,
          engineers: 5,
          storage: '100GB',
          features: [
            'Up to 5 engineers',
            '100GB storage',
            'Basic support',
            'Project templates',
            'Real-time collaboration',
          ],
        },
        {
          id: 'PROFESSIONAL',
          name: 'Professional',
          price: 99,
          engineers: 20,
          storage: '500GB',
          recommended: true,
          features: [
            'Up to 20 engineers',
            '500GB storage',
            'Priority support',
            'Advanced analytics',
            'Custom branding',
            'API access',
          ],
        },
        {
          id: 'ENTERPRISE',
          name: 'Enterprise',
          price: 299,
          engineers: -1, // Unlimited
          storage: '2TB',
          features: [
            'Unlimited engineers',
            '2TB storage',
            'Dedicated support',
            'SSO integration',
            'Advanced security',
            'Custom contracts',
            'SLA guarantee',
          ],
        },
      ]);
    }
  };

  const handleNext = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    if (activeStep === steps.length - 1) {
      // Final step - submit to Stripe
      await handleStripeCheckout();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      // If on first step, go back to login page
      navigate('/login');
    } else {
      // Otherwise, go to previous step
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleStripeCheckout = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const formData = getValues();
      
      // Prepare the signup data
      const signupData = {
        ...formData,
        billingEmail: formData.billingEmail || formData.adminEmail,
        successUrl: `${window.location.origin}/studio-signup/success`,
        cancelUrl: `${window.location.origin}/studio-signup/cancel`,
      };

      // Initiate signup with backend
      const response = await api.post('/studio-signup/initiate', signupData);
      
      if (response.data.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to initiate signup. Please try again.');
      setIsLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Tell us about your studio
            </Typography>
            
            <TextField
              fullWidth
              label="Studio Name"
              margin="normal"
              {...register('studioName')}
              error={!!errors.studioName}
              helperText={errors.studioName?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Company Name (Optional)"
              margin="normal"
              {...register('companyName')}
              error={!!errors.companyName}
              helperText={errors.companyName?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Studio Description (Optional)"
              margin="normal"
              multiline
              rows={4}
              {...register('studioDescription')}
              error={!!errors.studioDescription}
              helperText={errors.studioDescription?.message || 'Describe your studio and the type of work you do'}
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Create your admin account
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  margin="normal"
                  {...register('adminFirstName')}
                  error={!!errors.adminFirstName}
                  helperText={errors.adminFirstName?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  margin="normal"
                  {...register('adminLastName')}
                  error={!!errors.adminLastName}
                  helperText={errors.adminLastName?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              margin="normal"
              {...register('adminEmail')}
              error={!!errors.adminEmail}
              helperText={errors.adminEmail?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              margin="normal"
              {...register('adminPassword')}
              error={!!errors.adminPassword}
              helperText={errors.adminPassword?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              margin="normal"
              {...register('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Choose your plan
            </Typography>
            
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedPlan || ''}
                onChange={(e) => setValue('plan', e.target.value as any)}
              >
                <Grid container spacing={3}>
                  {plans.map((plan) => (
                    <Grid item xs={12} md={6} lg={4} key={plan.id}>
                      <Paper
                        sx={{
                          p: 3,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: selectedPlan === plan.id ? 'primary.main' : 'divider',
                          position: 'relative',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover',
                          },
                        }}
                        onClick={() => setValue('plan', plan.id as any)}
                      >
                        {plan.recommended && (
                          <Chip
                            label="Recommended"
                            color="primary"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                            }}
                          />
                        )}
                        
                        <FormControlLabel
                          value={plan.id}
                          control={<Radio />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Typography variant="h6" gutterBottom>
                                {plan.name}
                              </Typography>
                              <Typography variant="h4" color="primary" gutterBottom>
                                ${plan.price}
                                <Typography component="span" variant="body2" color="text.secondary">
                                  /month
                                </Typography>
                              </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Chip
                                  icon={<Group />}
                                  label={plan.engineers === -1 ? 'Unlimited engineers' : `${plan.engineers} engineers`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  icon={<Storage />}
                                  label={plan.storage}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                              
                              <List dense>
                                {plan.features.map((feature, index) => (
                                  <ListItem key={index} sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                      <Check color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={feature} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          }
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </FormControl>
            
            {errors.plan && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.plan.message}
              </Alert>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Billing Information (Optional)
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              You'll be redirected to Stripe to complete payment. You can update billing details there.
            </Alert>
            
            <TextField
              fullWidth
              label="Billing Email (Optional)"
              type="email"
              margin="normal"
              {...register('billingEmail')}
              error={!!errors.billingEmail}
              helperText={errors.billingEmail?.message || 'Leave blank to use admin email'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Tax ID (Optional)"
              margin="normal"
              {...register('taxId')}
              error={!!errors.taxId}
              helperText={errors.taxId?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business />
                  </InputAdornment>
                ),
              }}
            />

            <Divider sx={{ my: 3 }} />
            
            <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              
              {selectedPlan && plans.find(p => p.id === selectedPlan) && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Plan:</Typography>
                    <Typography fontWeight="bold">
                      {plans.find(p => p.id === selectedPlan)?.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Monthly Price:</Typography>
                    <Typography fontWeight="bold" color="primary">
                      ${plans.find(p => p.id === selectedPlan)?.price}/month
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="success" icon={<Check />}>
                    14-day free trial included
                  </Alert>
                </Box>
              )}
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0E1A',
        backgroundImage: `
          radial-gradient(ellipse at top right, rgba(255, 107, 53, 0.1) 0%, transparent 50%),
          radial-gradient(ellipse at bottom left, rgba(142, 36, 170, 0.1) 0%, transparent 50%)
        `,
        padding: 2,
      }}
    >
      <Card sx={{ 
        maxWidth: 800, 
        width: '100%',
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              mb: 3,
              padding: 2,
              borderRadius: '50%',
              background: 'rgba(255, 107, 53, 0.1)',
              border: '2px solid rgba(255, 107, 53, 0.3)',
            }}>
              <MusicNote sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Create Your Studio
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Join Abydos Studio Hub and start collaborating
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(handleNext)}>
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ mr: 1 }}
              >
                {activeStep === 0 ? 'Back to Login' : 'Back'}
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                endIcon={activeStep === steps.length - 1 ? <CreditCard /> : <ArrowForward />}
                sx={{
                  background: activeStep === steps.length - 1 
                    ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
                    : 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                  boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 24px rgba(255, 107, 53, 0.4)',
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                  'Complete Signup'
                ) : (
                  'Next'
                )}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};